import { Typography } from "@mui/material"
import type { ColumnDef, ExpandedState } from "@tanstack/react-table"
import React, { useCallback, useRef, useState } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { Location } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import ExpandedLocationRow from "@/screens/Companies/edit/components/locations/ExpandedLocationRow"
import { useLocationTableColumns } from "@/screens/Companies/edit/components/locations/hooks/useLocationTableColumns"
import type {
  LocationConfirmationDialogs,
  LocationMenuDialogs,
} from "@/screens/Companies/edit/components/locations/types/dialogs"

interface LocationsTableProps {
  locations: Location[]
  loadMore: () => void
  isLocationsFetching: boolean
  error?: { message: string }
  setOpenedDialog: (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => void
  setActiveEditLocation: (location: Location | null | undefined) => void
  reexecuteLocationListQuery: () => void
  isCustomerCompanyType: boolean
}

const LocationsTable: React.FC<LocationsTableProps> = ({
  locations,
  loadMore,
  isLocationsFetching,
  error,
  setOpenedDialog,
  setActiveEditLocation,
  reexecuteLocationListQuery,
  isCustomerCompanyType,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [expandedRow, setExpandedRow] = useState<ExpandedState>({})
  const columns = useLocationTableColumns(
    setOpenedDialog,
    setActiveEditLocation,
    reexecuteLocationListQuery,
    isCustomerCompanyType
  )

  const [throttledLoadMore] = useThrottle(() => loadMore(), 200)
  const scrollHandler = useScrollLoadMore({
    fetching: isLocationsFetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })
  const onScroll = useCallback(scrollHandler, [scrollHandler])

  return (
    <>
      <AerosBaseTable<Location>
        slotProps={{
          bodyCellExpanded: { className: "bg-primary-100" },
          expandedCell: expandedCellStyles,
          container: {
            ref: tableContainerRef,
            onScroll,
          },
        }}
        features={{
          table: {
            stickyHeader: true,
          },
          tanstackOptions: {
            data: locations,
            columns: columns as ColumnDef<Location>[],
            state: {
              expanded: expandedRow,
            },
            onExpandedChange: setExpandedRow,
            getRowCanExpand: () => true,
            renderExpandedContent: (row) => (
              <ExpandedLocationRow
                row={row}
                setActiveEditLocation={setActiveEditLocation}
                setDialogOpened={setOpenedDialog}
              />
            ),
          },
        }}
        error={error}
        noDataMessage='No locations found'
      />
      {error && <Typography color='error'>{error.message}</Typography>}
    </>
  )
}

export default LocationsTable
